/**
 * Modal for creating a new paper trading session.
 *
 * Phase 3: Unified mode — select a strategy config (or a multi-asset aggregation).
 * The "From Aggregation / Single Strategy" toggle is removed.
 *
 * LT gating: the "Bybit Live" connector is shown but disabled (coming soon) for
 * non-owner users. Owner = user.role === 'admin' || user.id === 'root'.
 * Server-side enforcement is handled by a parallel lane; this is the UI gate.
 */

import { useState, useEffect } from 'react';
import { getAggregations, getStrategyConfigs } from '../../api/client';
import { useQuery } from '@tanstack/react-query';
import { useCreatePaperSession } from '../../hooks/usePaperTrading';
import { useCredentials } from '../../hooks/useCredentials';
import { useUserStrategies } from '../../hooks/useUserStrategies';
import { useAuthStore } from '../../stores/authStore';
import type { StrategyConfigListItem } from '../../types';

interface CreatePaperSessionModalProps {
  onClose: () => void;
  onCreated?: (sessionId: string) => void;
}

/** Returns true when the current user is allowed to use the live (Bybit) connector. */
function useIsLiveTradingEnabled(): boolean {
  const user = useAuthStore((s) => s.user);
  if (!user) return false;
  return user.role === 'admin' || user.id === 'root';
}

export function CreatePaperSessionModal({ onClose, onCreated }: CreatePaperSessionModalProps) {
  const { data: aggregations, isLoading: loadingAggregations } = useQuery({
    queryKey: ['aggregations'],
    queryFn: getAggregations,
  });

  const { data: userStrategies, isLoading: loadingUserStrategies } = useUserStrategies();

  const createMutation = useCreatePaperSession();
  const { data: credentials } = useCredentials();
  const isLiveEnabled = useIsLiveTradingEnabled();

  // Shared
  const [name, setName] = useState('');
  const [initialCapital, setInitialCapital] = useState<number>(10000);
  const [initialCapitalStr, setInitialCapitalStr] = useState<string>('10000');

  // SHA-28: slippage percent — optional; empty string means "use server default"
  // (0.05% futures / 0% spot, identical to prior behavior).
  const [slippagePercentStr, setSlippagePercentStr] = useState<string>('');

  // Connector type + credential
  const [connectorType, setConnectorType] = useState<'paper' | 'bybit'>('paper');
  const [credentialId, setCredentialId] = useState<string>('');

  // Strategy selection (unified: pick user strategy then config)
  const [selectedUserStrategyId, setSelectedUserStrategyId] = useState('');
  const [selectedConfigId, setSelectedConfigId] = useState('');
  const [exchange, setExchange] = useState('bybit');

  // Optional: multi-asset aggregation config (only shown when aggregations exist with >1 sub)
  const [aggregationConfigId, setAggregationConfigId] = useState('');

  // Whether the user has explicitly chosen an aggregation vs a single config
  // This mirrors the "mode" concept but is implicit: if aggregationConfigId is set, use agg mode.
  const useAggregationMode = !!aggregationConfigId;

  // Fetch configs for the selected user strategy
  const { data: strategyConfigs, isLoading: loadingConfigs } = useQuery({
    queryKey: ['strategy-configs', { userStrategyId: selectedUserStrategyId }],
    queryFn: () => getStrategyConfigs({ userStrategyId: selectedUserStrategyId }),
    enabled: !!selectedUserStrategyId,
  });

  // Find the selected config object
  const selectedConfig: StrategyConfigListItem | undefined = strategyConfigs?.find(
    (c) => c.id === selectedConfigId,
  );

  // Multi-asset aggregations (those with more than 1 sub)
  const multiAssetAggregations = (aggregations ?? []).filter((a) => a.subStrategies.length > 1);

  // When user strategy changes, reset config selection
  useEffect(() => {
    setSelectedConfigId('');
  }, [selectedUserStrategyId]);

  // When aggregation selection changes, reset single-strategy selections
  useEffect(() => {
    if (aggregationConfigId) {
      setSelectedUserStrategyId('');
      setSelectedConfigId('');
    }
  }, [aggregationConfigId]);

  // When single-strategy selections change, clear aggregation
  useEffect(() => {
    if (selectedUserStrategyId || selectedConfigId) {
      setAggregationConfigId('');
    }
  }, [selectedUserStrategyId, selectedConfigId]);

  const needsCredential = connectorType === 'bybit';

  // Filter credentials to only show mainnet ones
  const filteredCredentials = (credentials ?? []).filter((c) => !c.testnet);

  const isSubmitDisabled = (() => {
    if (!name.trim()) return true;
    if (createMutation.isPending) return true;
    if (needsCredential && !credentialId) return true;
    if (useAggregationMode) return !aggregationConfigId;
    return !selectedConfigId;
  })();

  // SHA-28: parsed slippage override — undefined means "use server default"
  // (0.05% futures / 0% spot). Only meaningful for the simulated 'paper'
  // connector — live (bybit) fills are real exchange fills, not modeled slippage.
  const parsedSlippagePercent =
    connectorType === 'paper' && slippagePercentStr.trim() !== ''
      ? parseFloat(slippagePercentStr)
      : undefined;
  const slippagePercentInvalid =
    connectorType === 'paper' &&
    slippagePercentStr.trim() !== '' &&
    (isNaN(parsedSlippagePercent as number) || (parsedSlippagePercent as number) < 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitDisabled || slippagePercentInvalid) return;

    const connectorPayload = {
      connectorType,
      credentialId: needsCredential && credentialId ? credentialId : null,
    };
    const slippagePayload =
      parsedSlippagePercent !== undefined ? { slippagePercent: parsedSlippagePercent } : {};

    try {
      if (useAggregationMode) {
        const session = await createMutation.mutateAsync({
          name: name.trim(),
          aggregationConfigId,
          initialCapital,
          ...connectorPayload,
          ...slippagePayload,
        });
        onCreated?.(session.id);
      } else {
        const session = await createMutation.mutateAsync({
          name: name.trim(),
          strategyConfigId: selectedConfigId,
          exchange: exchange.trim(),
          ...(connectorType === 'paper' ? { initialCapital } : {}),
          ...connectorPayload,
          ...slippagePayload,
        });
        onCreated?.(session.id);
      }
      onClose();
    } catch {
      // Error shown below via createMutation.error
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const inputClass =
    'w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={handleBackdropClick}
    >
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-white">New Trading Session</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
            aria-label="Close modal"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Session Name */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Session Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. BTC 4h Paper Test"
              className={inputClass}
              required
            />
          </div>

          {/* User Strategy Dropdown */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Strategy</label>
            {loadingUserStrategies ? (
              <div className="text-sm text-gray-500">Loading strategies...</div>
            ) : (
              <select
                value={selectedUserStrategyId}
                onChange={(e) => setSelectedUserStrategyId(e.target.value)}
                className={inputClass}
                disabled={!!aggregationConfigId}
              >
                <option value="">Select a strategy...</option>
                {userStrategies?.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            )}
            {userStrategies?.length === 0 && (
              <p className="text-xs text-amber-400 mt-1">
                No strategies found. Create one via the MCP or dashboard first.
              </p>
            )}
          </div>

          {/* Configuration Dropdown (appears after strategy is selected) */}
          {selectedUserStrategyId && (
            <div>
              <label className="block text-sm text-gray-400 mb-1">Configuration</label>
              {loadingConfigs ? (
                <div className="text-sm text-gray-500">Loading configs...</div>
              ) : (
                <select
                  value={selectedConfigId}
                  onChange={(e) => setSelectedConfigId(e.target.value)}
                  className={inputClass}
                  required
                >
                  <option value="">Select a configuration...</option>
                  {strategyConfigs?.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} — {c.symbol} {c.timeframe}
                    </option>
                  ))}
                </select>
              )}
              {strategyConfigs && strategyConfigs.length === 0 && (
                <p className="text-xs text-amber-400 mt-1">
                  No configurations found for this strategy. Run a backtest first to create one.
                </p>
              )}
            </div>
          )}

          {/* Read-only config summary */}
          {selectedConfig && (
            <div className="bg-gray-700/50 rounded p-3 border border-gray-600 space-y-2">
              <div className="text-xs text-gray-400 uppercase tracking-wide font-medium">Config Details</div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <div className="text-xs text-gray-500">Symbol</div>
                  <div className="text-white font-medium">{selectedConfig.symbol}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Timeframe</div>
                  <div className="text-white font-medium">{selectedConfig.timeframe}</div>
                </div>
              </div>
              {Object.keys(selectedConfig.params).length > 0 && (
                <div>
                  <div className="text-xs text-gray-500 mb-1">Parameters</div>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(selectedConfig.params).map(([key, val]) => (
                      <span
                        key={key}
                        className="inline-flex items-center gap-1 bg-gray-600/60 rounded px-2 py-0.5 text-xs"
                      >
                        <span className="text-gray-400">{key}:</span>
                        <span className="text-white">{String(val)}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {(selectedConfig.latestRunSharpe !== undefined || selectedConfig.latestRunReturn !== undefined) && (
                <div className="flex gap-4 pt-1 border-t border-gray-600 text-xs">
                  {selectedConfig.latestRunReturn !== undefined && (
                    <div>
                      <span className="text-gray-500">Last Return: </span>
                      <span className={selectedConfig.latestRunReturn >= 0 ? 'text-green-400' : 'text-red-400'}>
                        {selectedConfig.latestRunReturn >= 0 ? '+' : ''}{selectedConfig.latestRunReturn.toFixed(1)}%
                      </span>
                    </div>
                  )}
                  {selectedConfig.latestRunSharpe !== undefined && (
                    <div>
                      <span className="text-gray-500">Sharpe: </span>
                      <span className="text-gray-200">{selectedConfig.latestRunSharpe.toFixed(2)}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-500">Runs: </span>
                    <span className="text-gray-200">{selectedConfig.runCount}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Multi-asset aggregation (shown only when multi-asset agg configs exist) */}
          {multiAssetAggregations.length > 0 && (
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                Multi-asset portfolio{' '}
                <span className="text-gray-600 font-normal">(optional — overrides strategy above)</span>
              </label>
              {loadingAggregations ? (
                <div className="text-sm text-gray-500">Loading...</div>
              ) : (
                <select
                  value={aggregationConfigId}
                  onChange={(e) => setAggregationConfigId(e.target.value)}
                  className={inputClass}
                >
                  <option value="">None — use single strategy above</option>
                  {multiAssetAggregations.map((agg) => (
                    <option key={agg.id} value={agg.id}>
                      {agg.name} — {agg.subStrategies.length} assets, ${agg.initialCapital.toLocaleString()}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Exchange (only relevant for single-strategy mode) */}
          {!useAggregationMode && (
            <div>
              <label className="block text-sm text-gray-400 mb-1">Exchange</label>
              <input
                type="text"
                value={exchange}
                onChange={(e) => setExchange(e.target.value)}
                placeholder="bybit"
                className={inputClass}
                required
              />
            </div>
          )}

          {/* Connector Type */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Connector</label>
            <div className="flex rounded overflow-hidden border border-gray-600 text-sm">
              <button
                type="button"
                onClick={() => {
                  setConnectorType('paper');
                  setCredentialId('');
                }}
                className={`flex-1 py-2 transition-colors font-medium ${
                  connectorType === 'paper'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-700 text-gray-400 hover:text-white'
                }`}
              >
                Paper (Simulated)
              </button>

              {/* Bybit Live — owner-only. Non-owners see it disabled with "Coming soon" badge. */}
              <button
                type="button"
                data-testid="connector-bybit"
                onClick={() => {
                  if (!isLiveEnabled) return; // gate: non-owner can't activate
                  setConnectorType('bybit');
                  setCredentialId('');
                }}
                disabled={!isLiveEnabled}
                className={`flex-1 py-2 transition-colors font-medium border-l border-gray-600 ${
                  connectorType === 'bybit' && isLiveEnabled
                    ? 'bg-primary-600 text-white'
                    : isLiveEnabled
                      ? 'bg-gray-700 text-gray-400 hover:text-white'
                      : 'bg-gray-800 text-gray-600 cursor-not-allowed'
                }`}
              >
                Bybit Live
                {!isLiveEnabled && (
                  <span className="ml-1.5 text-xs bg-gray-700 text-gray-500 px-1.5 py-0.5 rounded border border-gray-600">
                    Coming soon
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Credential selector — only shown for bybit */}
          {needsCredential && isLiveEnabled && (
            <div>
              <label className="block text-sm text-gray-400 mb-1">API Credential</label>
              {credentials && credentials.length === 0 ? (
                <p className="text-xs text-amber-400">
                  No API keys configured. Add one in the API Keys panel on the Trading page.
                </p>
              ) : filteredCredentials.length === 0 ? (
                <p className="text-xs text-amber-400">
                  No mainnet credentials found. Check the API Keys panel.
                </p>
              ) : (
                <select
                  value={credentialId}
                  onChange={(e) => setCredentialId(e.target.value)}
                  className={inputClass}
                  required
                >
                  <option value="">Select an API key...</option>
                  {filteredCredentials.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label} — ...{c.maskedKey}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Initial Capital — only for paper trading */}
          {connectorType === 'paper' && (
            <div>
              <label className="block text-sm text-gray-400 mb-1">Initial Capital ($)</label>
              <input
                type="number"
                value={initialCapitalStr}
                onChange={(e) => {
                  setInitialCapitalStr(e.target.value);
                  const parsed = parseFloat(e.target.value);
                  if (!isNaN(parsed)) setInitialCapital(parsed);
                }}
                onBlur={() => {
                  if (!initialCapitalStr || isNaN(parseFloat(initialCapitalStr))) {
                    setInitialCapital(10000);
                    setInitialCapitalStr('10000');
                  }
                }}
                min={1}
                step="any"
                className={inputClass}
              />
            </div>
          )}

          {/* Slippage — only for paper trading (SHA-28). Simulated fills only;
              live/real-money fills use the exchange's actual slippage. */}
          {connectorType === 'paper' && (
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                Slippage (%){' '}
                <span className="text-gray-600 font-normal">(optional — default: 0.05% futures / 0% spot)</span>
              </label>
              <input
                type="number"
                data-testid="slippage-percent-input"
                value={slippagePercentStr}
                onChange={(e) => setSlippagePercentStr(e.target.value)}
                placeholder="0.05"
                min={0}
                step="any"
                className={`${inputClass} ${slippagePercentInvalid ? 'border-red-500' : ''}`}
              />
              {slippagePercentInvalid && (
                <p className="text-xs text-red-400 mt-1">Slippage must be a non-negative number.</p>
              )}
            </div>
          )}

          {/* Error */}
          {createMutation.isError && (
            <div className="bg-red-900/50 border border-red-700 rounded p-2 text-sm text-red-300">
              {createMutation.error instanceof Error
                ? createMutation.error.message
                : 'Failed to create session'}
            </div>
          )}

          {/* Version pinning note */}
          <p className="text-xs text-gray-500 italic">
            Strategy versions will be pinned to current at session creation time.
            The session is immutable to later strategy updates.
          </p>

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 rounded border border-gray-600 text-gray-300 hover:text-white hover:border-gray-500 transition-colors text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitDisabled || slippagePercentInvalid}
              className="flex-1 py-2 rounded bg-primary-600 hover:bg-primary-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium text-sm transition-colors"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Session'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreatePaperSessionModal;
